import { useEffect, useState, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getProductById } from '../api/productApi';
import { CartContext } from '../context/CartContext';
import Toast from '../components/Toast';
import '../styles/ProductDetails.css';

const ProductDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addToCart } = useContext(CartContext);
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(0);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [toast, setToast] = useState(null);

  useEffect(() => {
    fetchProduct();
  }, [id]);

  useEffect(() => {
    if (product && product.variants && product.variants.length > 0) {
      // Sort variants by weight and select the smallest
      const sortedVariants = [...product.variants].sort((a, b) => parseFloat(a.weight) - parseFloat(b.weight));
      setSelectedVariant(sortedVariants[0]);
    }
  }, [product]);

  const fetchProduct = async () => {
    try {
      const data = await getProductById(id);
      setProduct(data);
    } catch (error) {
      console.error('Error fetching product:', error);
      // Fallback demo product
      setProduct({
        _id: id,
        id: parseInt(id),
        productName: 'Sample Product',
        name: 'Sample Product',
        price: 8299,
        description: 'This is a detailed description of the product. It includes all the features and specifications that make this product unique and valuable.',
        productImage: 'https://via.placeholder.com/500',
        image: 'https://via.placeholder.com/500'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleQuantityChange = (change) => {
    const newQuantity = quantity + change;
    if (newQuantity >= 0 && newQuantity <= 99) {
      setQuantity(newQuantity);
    }
  };

  const handleVariantChange = (e) => {
    const variantId = e.target.value;
    const variant = product.variants.find(v => v._id === variantId);
    setSelectedVariant(variant);
  };

  // Normalize unit names
  const normalizeUnit = (unit) => {
    if (!unit) return unit;
    if (unit.toLowerCase() === 'gm') return 'gms';
    return unit;
  };

  const handleAddToCart = () => {
    if (quantity === 0) {
      setToast({ message: 'Please select quantity', type: 'warning' });
      return;
    }
    
    // Create product with selected variant
    const productToAdd = {
      ...product,
      selectedVariant: selectedVariant,
      variantPrice: selectedVariant?.price || product.price,
      variantWeight: selectedVariant?.weight,
      price: selectedVariant?.price || product.price
    };
    
    // Add to cart with the selected quantity
    addToCart(productToAdd, quantity);
    
    const productName = product.productName || product.name;
    const weightText = selectedVariant ? ` (${selectedVariant.weight}${normalizeUnit(selectedVariant.unit)})` : '';
    setToast({ 
      message: `${quantity} × ${productName}${weightText} added to cart successfully!`, 
      type: 'success' 
    });
    
    setQuantity(0);
  };

  if (loading) {
    return <div className="loading">Loading product...</div>;
  }

  if (!product) {
    return <div className="error">Product not found</div>;
  }

  // Handle both API formats
  const productName = product.productName || product.name;
  const productImage = product.productImage || product.image;
  const productDescription = product.description;
  
  // Use variant price if available, otherwise use product price
  const displayPrice = selectedVariant?.price || product.price;
  const displayWeight = selectedVariant?.weight;
  
  // Sort variants by weight
  const sortedVariants = product.variants ? [...product.variants].sort((a, b) => parseFloat(a.weight) - parseFloat(b.weight)) : [];
  
  // Construct image URL for backend uploads
  const UPLOAD_URL = process.env.REACT_APP_UPLOAD_URL;
  const imageUrl = productImage?.startsWith('uploads') 
    ? `${UPLOAD_URL}/${productImage.replace(/\\/g, '/')}` 
    : productImage || '/item-image.png';

  const totalPrice = displayPrice * quantity;
  
  // Check if selected variant is out of stock
  const isOutOfStock = selectedVariant ? selectedVariant.quantity === 0 : false;

  return (
    <>
      {toast && (
        <Toast 
          message={toast.message} 
          type={toast.type} 
          onClose={() => setToast(null)} 
        />
      )}
      <div className="product-details">
      <div className="product-container">
        <div className={`product-image ${isOutOfStock ? 'out-of-stock' : ''}`}>
          <img src={imageUrl} alt={productName} />
          {isOutOfStock && (
            <div className="stock-badge out-of-stock-badge">
              Out of Stock
            </div>
          )}
        </div>
        
        <div className="product-info">
          <h1>{productName}</h1>
          <div className="price-section">
            <p className="unit-price">
              ₹{displayPrice}
              {displayWeight && <span className="per-unit"> / {displayWeight}{normalizeUnit(selectedVariant?.unit)}</span>}
            </p>
            {quantity > 1 && (
              <p className="total-price">Total: ₹{totalPrice.toFixed(2)}</p>
            )}
          </div>
          <p className="description">{productDescription}</p>

          {/* Weight/Variant Selector */}
          {sortedVariants.length > 0 && (
            <div className="variant-selector-detail">
              <label>Select Weight:</label>
              <select value={selectedVariant?._id || ''} onChange={handleVariantChange}>
                {sortedVariants.map(variant => (
                  <option key={variant._id} value={variant._id}>
                    {variant.weight}{normalizeUnit(variant.unit)} - ₹{variant.price}
                    {variant.quantity <= 5 && variant.quantity > 0 && ` (Only ${variant.quantity} left)`}
                    {variant.quantity === 0 && ' (Out of Stock)'}
                  </option>
                ))}
              </select>
            </div>
          )}
          
          <div className="quantity-section">
            <label>Quantity:</label>
            <div className="quantity-selector">
              <button 
                className="qty-btn" 
                onClick={() => handleQuantityChange(-1)}
                disabled={quantity <= 0 || isOutOfStock}
              >
                -
              </button>
              <input 
                type="number" 
                value={quantity} 
                onChange={(e) => {
                  const val = parseInt(e.target.value) || 0;
                  if (val >= 0 && val <= 99) setQuantity(val);
                }}
                min="0"
                max="99"
                disabled={isOutOfStock}
              />
              <button 
                className="qty-btn" 
                onClick={() => handleQuantityChange(1)}
                disabled={quantity >= 99 || isOutOfStock}
              >
                +
              </button>
            </div>
          </div>
          
          <div className="product-actions">
            <button 
              onClick={handleAddToCart} 
              className="btn-add-cart"
              disabled={quantity === 0 || isOutOfStock}
            >
              {isOutOfStock ? 'Out of Stock' : 'Add to Cart'}
            </button>
            <button onClick={() => navigate('/products')} className="btn-back">
              Back to Products
            </button>
          </div>
        </div>
      </div>
    </div>
    </>
  );
};

export default ProductDetails;
